import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function PayPalReturn() {
  const [params] = useSearchParams();
  const orderId = params.get("token");
  const { refresh } = useAuth();
  const [state, setState] = useState(orderId ? "capturing" : "error");
  const [message, setMessage] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (!orderId || started.current) return;
    started.current = true;
    api.post("/payments/paypal/capture", { order_id: orderId })
      .then(() => { setState("paid"); refresh(); })
      .catch((err) => { setState("error"); setMessage(errText(err, "PayPal couldn't complete the payment.")); });
  }, [orderId, refresh]);

  return (
    <div className="min-h-screen paper-noise flex items-center justify-center px-6">
      <div className="max-w-lg w-full border border-foreground/15 bg-background p-10 shadow-[8px_8px_0_0_hsl(var(--primary))]" data-testid="paypal-status-card">
        {state === "capturing" && (<>
          <Loader2 className="w-10 h-10 animate-spin text-primary mb-6" />
          <h1 className="font-serif-display text-4xl tracking-tight mb-3">Finishing with PayPal…</h1>
          <p className="text-muted-foreground">Capturing your payment. This takes a couple of seconds.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-10 h-10 text-primary mb-6" strokeWidth={1.5} />
          <div className="font-mono-label text-muted-foreground mb-2">Payment confirmed</div>
          <h1 className="font-serif-display text-4xl tracking-tight mb-3" data-testid="paypal-success-title">Welcome to your new plan.</h1>
          <p className="text-muted-foreground mb-8">Your prepaid period is active — no auto-renewal, we'll remind you before it ends.</p>
          <Link to="/app" data-testid="paypal-go-dashboard"><Button className="rounded-sm bg-foreground text-background hover:bg-primary btn-invert">Go to library →</Button></Link>
        </>)}
        {state === "error" && (<>
          <XCircle className="w-10 h-10 text-destructive mb-6" strokeWidth={1.5} />
          <h1 className="font-serif-display text-4xl tracking-tight mb-3" data-testid="paypal-error-title">Payment not completed.</h1>
          <p className="text-muted-foreground mb-8" data-testid="paypal-error-message">{message || "This link is missing its PayPal order. Start again from the pricing page."}</p>
          <Link to="/#pricing"><Button variant="outline" className="rounded-sm">Back to pricing</Button></Link>
        </>)}
      </div>
    </div>
  );
}
